import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Row,
  Tooltip,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import * as DS from "redux-store/device-statuses/selectors";
import * as Device from "redux-store/devices/selectors";
import * as User from "redux-store/users/selectors";
import semverLt from "semver/functions/lt";
import semverValid from "semver/functions/valid";
import DialogModal from "ui/dialog-modal";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { ConnectedWsDeviceStatusConnStatus } from "ui/ws-ds-conn-status/connected-ws-ds-conn-status";
import { getErrorMessage } from "util/response";
import { Page } from "view/layout/page";
import { VIEW } from "view/views";

const Dialogs = {
  CLEAR_CACHE: "clear-cache",
  CLEAR_WEBCAM_CACHE: "clear-webcam-cache",
  RESTART: "restart",
};

class DeviceViewPage extends React.PureComponent {
  static get propTypes() {
    return {
      activeTab: PropTypes.string,
      children: PropTypes.node,
      clearPlayerCache: PropTypes.func.isRequired,
      clearWebcamSnapshotCache: PropTypes.func,
      device: PropTypes.object,
      deviceId: PropTypes.string.isRequired,
      deviceStatus: PropTypes.object.isRequired,
      featureFlags: PropTypes.object,
      isThirdParty: PropTypes.bool.isRequired,
      loadDeviceStats: PropTypes.func.isRequired,
      restartDevice: PropTypes.func.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      unloadDeviceStats: PropTypes.func.isRequired,
      user: PropTypes.object,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      activeDialogModal: null,
      errorMessage: "",
      loading: false,
      refreshing: false,
      restarting: false,
      showDialogModal: false,
      successMessage: "",
    };

    this._loadDeviceStats = this._loadDeviceStats.bind(this);
    this._isMounted = false;
    this._refresh = this._refresh.bind(this);
    this._restartDevice = this._restartDevice.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this._refresh(this.props);
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.device && prevProps.device.id;
    const currId = this.props.device && this.props.device.id;

    if (prevId !== currId && !this.state.refreshing) {
      this._refresh(this.props);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    const { unloadDeviceStats } = this.props;

    unloadDeviceStats();
  }

  _isReadOnly() {
    return (
      <Tooltip id="tooltip">Feature unavailable to readonly user.</Tooltip>
    );
  }

  _refresh(props) {
    const { device } = props;
    if (!device) {
      return;
    }

    if (this._isMounted) {
      this.setState({ refreshing: true }, this._loadDeviceStats);
    }
  }

  _loadDeviceStats() {
    const { deviceId, loadDeviceStats } = this.props;

    return loadDeviceStats(deviceId)
      .then(() => {
        if (!this._isMounted) return;
        this.setState({ errorMessage: "" });
      })
      .catch((e) => {
        if (!this._isMounted) return;
        this.setState({ errorMessage: getErrorMessage(e) });
      })
      .finally(() => {
        if (!this._isMounted) return;
        this.setState({ refreshing: false });
      });
  }

  _restartDevice() {
    this.setState({ restarting: true }, this._restartDeviceAction);
  }

  _restartDeviceAction() {
    const { device, restartDevice } = this.props;

    return restartDevice(device.id)
      .then(() => {
        if (!this._isMounted) return;
        this.setState({
          errorMessage: "",
          successMessage: "The restart is initiated...",
        });
      })
      .catch((e) => {
        if (!this._isMounted) return;
        this.setState({
          errorMessage: getErrorMessage(e),
        });
      })
      .finally(() => {
        if (!this._isMounted) return;
        this.setState({
          restarting: false,
          showDialogModal: false,
        });
      });
  }

  _renderBadges() {
    const { device } = this.props;
    const badges = [];
    const needs_upgrade = Device.needsSoftwareUpgrade(device);
    const pending_upgrade =
      semverValid(device.target_version) &&
      semverValid(device.current_version) &&
      semverLt(device.current_version, device.target_version);
    const pending_downgrade =
      semverValid(device.target_version) &&
      semverValid(device.current_version) &&
      semverLt(device.target_version, device.current_version);

    if (pending_upgrade) {
      badges.push(
        <span key="upgrade" className="badge bg-orange qa-player-version-tag">
          Pending Upgrade to v{device.target_version}
        </span>,
      );
    } else if (pending_downgrade) {
      badges.push(
        <span key="upgrade" className="badge bg-orange qa-player-version-tag">
          Pending Downgrade to v{device.target_version}
        </span>,
      );
    } else if (needs_upgrade) {
      badges.push(
        <span key="upgrade" className="badge bg-red qa-player-version-tag">
          Needs Upgrade: v{device.current_version}
        </span>,
      );
    } else {
      badges.push(
        <span key="upgrade" className="badge bg-green qa-player-version-tag">
          v{device.current_version}
        </span>,
      );
    }

    if (Device.isSystemManaged(device)) {
      badges.push(
        <span key="system_managed" className="badge bg-red">
          Ad Platform Synced
        </span>,
      );
    }

    if (Device.isTesting(device)) {
      badges.push(
        <span key="testing" className="badge bg-gray">
          Testing
        </span>,
      );
    } else {
      badges.push(
        <span key="testing" className="badge bg-teal">
          Production
        </span>,
      );
    }

    badges.push(
      <Link key="network" to={`/networks/${device.network_id}`}>
        <span className="badge bg-aqua">{device.network_name}</span>
      </Link>,
    );

    device.tags.forEach((tag) => {
      const { name, id } = tag;

      if (name && name.length > 0 && name[0] !== "_") {
        badges.push(
          <Link key={`tag-${id}`} to={`/tags/${id}`}>
            <span className="badge bg-maroon">{name}</span>
          </Link>,
        );
      }
    });

    return badges;
  }

  _handleRestartClick(readonly) {
    if (readonly) {
      return;
    }
    this.setState({ activeDialogModal: Dialogs.RESTART });
  }

  _renderRestartDeviceBtn(readonly) {
    return (
      <Dropdown.Item
        onClick={() => this._handleRestartClick(readonly)}
        key="restartDevice"
        name="restart-device-button"
      >
        <i className="fa fa-power-off mr-2" />
        Restart Device
      </Dropdown.Item>
    );
  }

  _renderDeviceActionConfirmation() {
    if (!this.state.activeDialogModal) {
      return null;
    }

    const {
      clearPlayerCache,
      clearWebcamSnapshotCache,
      device,
      deviceStatus,
      restartDevice,
    } = this.props;

    const hide = () => {
      this.setState({
        activeDialogModal: null,
      });
    };

    if (!DS.isOnline(deviceStatus)) {
      const desc =
        "This player is currently offline.\n" +
        "This operation will have no effect.";
      return <DialogModal title="Alert" desc={desc} onHide={hide} />;
    }

    if (this.state.activeDialogModal === Dialogs.CLEAR_CACHE) {
      return (
        <DialogModal
          desc="This operation will clear the player cache."
          onHide={hide}
          onSuccess={() =>
            this._triggerAction(
              clearPlayerCache,
              "Clear cache operation initiated, please check back again shortly.",
              device.device_id,
            )
          }
          buttonLabel="Clear Player Cache"
        />
      );
    }

    if (this.state.activeDialogModal == Dialogs.CLEAR_WEBCAM_CACHE) {
      return (
        <DialogModal
          desc="This operation will cause the player to take new webcam snapshots."
          onHide={hide}
          onSuccess={() =>
            this._triggerAction(
              clearWebcamSnapshotCache,
              "Player snapshots reset",
              device.device_id,
            )
          }
          buttonLabel="Trigger new webcam snapshots."
        />
      );
    }

    if (this.state.activeDialogModal === Dialogs.RESTART) {
      return (
        <DialogModal
          desc="This operation will restart the player.
          On ChromeOS and WebOS, the operating system will be rebooted."
          onHide={hide}
          onSuccess={() =>
            this._triggerAction(
              restartDevice,
              "The restart is initiated, please check back again shortly.",
              device.id,
            )
          }
          buttonLabel="Restart Device"
        />
      );
    }
  }

  _handleClearPlayerCache(readonly) {
    if (readonly) {
      return;
    }
    this.setState({
      activeDialogModal: Dialogs.CLEAR_CACHE,
    });
  }

  _renderClearPlayerCacheBtn(readonly) {
    return (
      <Dropdown.Item
        onClick={() => this._handleClearPlayerCache(readonly)}
        key="clearPlayerCache"
        name="clear-player-cache-button"
      >
        <i className="fa fa-trash-o mr-2" />
        Clear Player Cache
      </Dropdown.Item>
    );
  }

  _handleClearWebcamCache(readonly) {
    if (readonly) {
      return;
    }
    this.setState({
      activeDialogModal: Dialogs.CLEAR_WEBCAM_CACHE,
    });
  }

  _renderClearWebcamCacheBtn(readonly) {
    return (
      <Dropdown.Item
        onClick={() => this._handleClearWebcamCache(readonly)}
        key="resetSnapshots"
        name="clear-webcam-cache-button"
      >
        <i className="fa fa-dot-circle-o mr-2" />
        Trigger new webcam snapshots
      </Dropdown.Item>
    );
  }

  _webcamSnapshotEnabled() {
    if (!this.props.featureFlags) {
      return false;
    }
    return this.props.featureFlags["enable-webcam-snapshots"] || false;
  }

  _toolbox() {
    const { deviceId, isThirdParty, user } = this.props;
    const { refreshing } = this.state;
    const readonly = User.isReadonly(user);
    const parameterTitle = readonly ? "View parameters" : "Update parameters";
    const flagsTitle = readonly ? "View flags" : "Update flags";

    const btnClassNames = classNames("fa fa-refresh", {
      "fa-spin": refreshing,
    });

    return (
      <div className="page-tools btn-group">
        <Button
          variant="info"
          name="refresh-button"
          onClick={() => this._refresh(this.props)}
          disabled={refreshing}
        >
          <i className={btnClassNames} /> Refresh
        </Button>
        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle variant="primary" id="dropdown-configure">
            <i className="fa fa-cog" /> Configure
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {!readonly && (
              <Dropdown.Item
                as={Link}
                to={`/devices/${deviceId}/${VIEW.EDIT}`}
                key="edit"
              >
                <i className="fa fa-edit mr-2" /> Edit device
              </Dropdown.Item>
            )}
            <Dropdown.Item
              as={Link}
              to={`/devices/${deviceId}/configure`}
              key="changeParams"
            >
              <i className="fa fa-list mr-2" /> {parameterTitle}
            </Dropdown.Item>
            {!isThirdParty && (
              <Dropdown.Item
                as={Link}
                to={`/devices/${deviceId}/flags`}
                key="changeFlags"
              >
                <i className="fa fa-flag mr-2" /> {flagsTitle}
              </Dropdown.Item>
            )}
            {this._webcamSnapshotEnabled() &&
              this._renderClearWebcamCacheBtn(readonly)}
            {!isThirdParty && this._renderClearPlayerCacheBtn(readonly)}
            {!isThirdParty && this._renderRestartDeviceBtn(readonly)}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  }

  _triggerAction(action, successMessage, deviceId) {
    this.setState({ loading: true });
    return action(deviceId)
      .then(() => {
        if (!this._isMounted) return;
        this.setState({
          errorMessage: "",
          successMessage: successMessage,
        });
      })
      .catch((e) => {
        if (!this._isMounted) return;
        this.setState({
          errorMessage: getErrorMessage(e),
        });
      })
      .finally(() => {
        if (!this._isMounted) return;
        this.setState({
          loading: false,
          activeDialogModal: null,
        });
      });
  }

  _renderView() {
    const { activeTab, children, device, deviceId, isThirdParty } = this.props;
    const { errorMessage, successMessage } = this.state;

    if (!device) {
      return null;
    }

    if (!Device.hasConnected(device)) {
      return (
        <Row>
          <Col xs={12}>
            <Alert variant="info">
              <i className="fa fa-info-circle mr-2" aria-hidden />
              This device has been registered, but it has not connected to our
              systems yet.
            </Alert>
          </Col>
        </Row>
      );
    }

    return (
      <div>
        <Row>
          <Col xs={12}>
            <ConnectedWsDeviceStatusConnStatus />
          </Col>
        </Row>
        <Notification error={errorMessage} success={successMessage} />
        <div className="nav-tabs-custom">
          <ul className="device-view nav nav-tabs">
            <li
              className={classNames({
                active: activeTab === "overview",
              })}
            >
              <Link to={`/devices/${deviceId}/overview`}>Overview</Link>
            </li>
            <li
              className={classNames({
                active: activeTab === "events",
              })}
            >
              <Link to={`/devices/${deviceId}/events`}>Events</Link>
            </li>
            {!isThirdParty && (
              <li
                className={classNames({
                  active: activeTab === "details",
                })}
              >
                <Link to={`/devices/${deviceId}/details`}>Details</Link>
              </li>
            )}
            <li
              className={classNames({
                active: activeTab === "internals",
              })}
            >
              <Link to={`/devices/${deviceId}/internals`}>Internal State</Link>
            </li>

            <li
              className={classNames({
                active: activeTab === "logs",
              })}
            >
              <Link to={`/devices/${deviceId}/logs`}>Logs</Link>
            </li>

            <li className="pull-right">
              <span className="device-overview-badges">
                {this._renderBadges()}
              </span>
            </li>
          </ul>
          <div className="tab-content">
            <div className="tab-pane active">{children}</div>
          </div>
        </div>
        {this._renderDeviceActionConfirmation()}
      </div>
    );
  }

  render() {
    const { device, deviceId, storesLoaded } = this.props;

    const title = (device && device.name) || "Device View";
    const toolbox = (device && this._toolbox()) || null;
    const view = storesLoaded ? this._renderView() : <NavLoader.Proxy />;

    return (
      <Page title={title} desc={deviceId} toolbox={toolbox}>
        {view}
      </Page>
    );
  }
}

export { DeviceViewPage };
